<template>
  <HeaderApp />
  <LoadingProgress v-show="isPageLoading" />
  <div
    :dir="isRtl ? 'rtl' : 'ltr'"
    :class="
      'mx-2 mt-14 mb-5 h-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 no-print ' +
      ($store.getters['isRtl']
        ? $store.state.navbar.isSidebarCollapsed
          ? ' md:mr-20 '
          : ' md:mr-60 '
        : $store.state.navbar.isSidebarCollapsed
        ? ' md:ml-20  '
        : ' md:ml-60  ')
    "
  >
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
        {{ $t("sarmaya_title") }}
      </h1>
    </div>

    <div
      class="space-y-6 px-4 sm:px-6 py-6"
      :class="{ 'opacity-0 pointer-events-none select-none': isPageLoading }"
      :aria-busy="isPageLoading"
    >
      <section aria-label="summary" class="space-y-6">
        <div v-for="block in summaryTotalsList" :key="block.key">
          <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
            {{ currencySectionTitle(block) }}
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div
              class="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm flex items-start gap-3 dark:border-emerald-800/60 dark:bg-gray-800/50"
            >
              <div class="sarmaya-icon-d shrink-0 rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-950/40">
                <i class="fa-solid fa-arrow-trend-up text-lg"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="sarmaya-txt-d-label text-xs font-medium">
                  {{ $t("sarmaya_total_deposit") }}
                </p>
                <p class="sarmaya-txt-d text-xl font-bold tabular-nums inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                  <span>{{ formatNumber(block.total_deposit || 0) }}</span>
                  <span class="text-sm font-semibold">{{ currencySymbol(block) }}</span>
                </p>
              </div>
            </div>
            <div
              class="rounded-xl border border-rose-200 bg-white p-4 shadow-sm flex items-start gap-3 dark:border-rose-800/60 dark:bg-gray-800/50"
            >
              <div class="sarmaya-icon-w shrink-0 rounded-lg bg-rose-50 p-2.5 dark:bg-rose-950/40">
                <i class="fa-solid fa-arrow-trend-down text-lg"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="sarmaya-txt-w-label text-xs font-medium">
                  {{ $t("sarmaya_total_withdraw") }}
                </p>
                <p class="sarmaya-txt-w text-xl font-bold tabular-nums inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                  <span>{{ formatNumber(block.total_withdraw || 0) }}</span>
                  <span class="text-sm font-semibold">{{ currencySymbol(block) }}</span>
                </p>
              </div>
            </div>
            <div
              class="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/90 p-4 flex items-start gap-3"
            >
              <div class="shrink-0 rounded-lg bg-slate-200/80 dark:bg-slate-700 p-2.5 text-slate-700 dark:text-slate-200">
                <i class="fa-solid fa-scale-balanced text-lg"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-slate-600 dark:text-slate-400">
                  {{ $t("sarmaya_total_now") }}
                </p>
                <p
                  class="text-xl font-bold text-slate-900 dark:text-white tabular-nums inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0"
                >
                  <span>{{ formatNumber(block.total_now || 0) }}</span>
                  <span class="text-sm font-semibold text-slate-600 dark:text-slate-300">{{
                    currencySymbol(block)
                  }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 p-4"
        aria-label="per-target-user"
      >
        <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
          {{ $t("sarmaya_per_target_user") }}
        </h2>
        <div v-if="hasPerTarget" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <div
            v-for="userGrp in perTargetByUserList"
            :key="'sarmaya-pt-' + String(userGrp.target_user_id != null ? userGrp.target_user_id : 'none')"
            class="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 p-3 shadow-sm"
          >
            <p class="font-semibold text-gray-900 dark:text-white mb-3 truncate border-b border-gray-100 pb-2 dark:border-gray-700">
              {{ userGrp.target_user ? userGrp.target_user.name : "—" }}
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div v-for="col in userGrp.columns" :key="col.curId">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  {{ col.label }}
                </p>
                <div class="flex flex-col gap-2 text-sm">
                  <div
                    class="flex items-center justify-between gap-2 rounded-lg border border-emerald-200/80 bg-emerald-50/90 px-2.5 py-1.5 dark:border-emerald-800/50 dark:bg-emerald-950/30"
                  >
                    <span class="sarmaya-txt-d-label font-medium">{{ $t("sarmaya_total_deposit") }}</span>
                    <span class="sarmaya-txt-d tabular-nums font-bold">
                      {{ formatNumber(col.item.total_deposit || 0) }}
                      <span class="text-xs font-semibold">{{ currencySymbol(col.item) }}</span>
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-2 rounded-lg border border-rose-200/80 bg-rose-50/90 px-2.5 py-1.5 dark:border-rose-800/50 dark:bg-rose-950/30"
                  >
                    <span class="sarmaya-txt-w-label font-medium">{{ $t("sarmaya_total_withdraw") }}</span>
                    <span class="sarmaya-txt-w tabular-nums font-bold">
                      {{ formatNumber(col.item.total_withdraw || 0) }}
                      <span class="text-xs font-semibold">{{ currencySymbol(col.item) }}</span>
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-600 dark:bg-slate-800/80"
                  >
                    <span class="text-slate-600 dark:text-slate-400">{{ $t("sarmaya_total_now") }}</span>
                    <span class="tabular-nums font-bold text-slate-900 dark:text-white">
                      {{ formatNumber(col.item.net_total != null ? col.item.net_total : 0) }}
                      <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">{{
                        currencySymbol(col.item)
                      }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          {{ $t("sarmaya_per_target_user_empty") }}
        </p>
      </section>

      <section
        class="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/40 p-4 sm:p-5"
        aria-label="add-transaction"
      >
        <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ $t("sarmaya_section_add") }}
          </h2>
          <button
            v-if="isEditing"
            type="button"
            class="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            @click="resetForm"
          >
            {{ $t("sarmaya_cancel_edit") }}
          </button>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
          <div class="lg:col-span-3">
            <DropdownInput
              v-model="form.target_user_id"
              :items="usersList"
              :label="$t('sarmaya_target_user')"
              :placeholder="$t('sarmaya_target_user')"
              :rules="rules"
            />
          </div>
          <div class="lg:col-span-2 sarmaya-form-type-dd" :class="formTypeDdClass">
            <DropdownInput
              v-model="form.sat_type"
              :items="typesList"
              :label="$t('sarmaya_type')"
              :placeholder="$t('sarmaya_type')"
              :rules="rules"
            />
          </div>
          <div class="lg:col-span-2">
            <DropdownInput
              v-model="form.cur_id"
              :items="formCurrencyList"
              :label="$t('sarmaya_currency')"
              :placeholder="$t('sarmaya_currency')"
              :rules="rules"
            />
          </div>
          <div class="lg:col-span-2">
            <BaseInput
              v-model="form.sat_amount"
              type="number"
              :label="$t('sarmaya_amount')"
              :placeholder="$t('sarmaya_amount')"
              :rules="rules"
            />
          </div>
          <div class="lg:col-span-2">
            <BaseInput
              v-model="form.sat_note"
              :label="$t('note')"
              :placeholder="$t('note')"
              :rules="[]"
            />
          </div>
          <div class="lg:col-span-1 flex items-end">
            <ButtonInput
              v-if="!isEditing && can('add')"
              class="w-full"
              type="button"
              @click="createItem"
              :isLoading="$store.state.sarmaya.isBtnLoading"
              :label="$t('add')"
            />
            <ButtonInput
              v-if="isEditing && can('edit')"
              class="w-full"
              type="button"
              @click="updateItem"
              :isLoading="$store.state.sarmaya.isBtnEditLoading"
              :label="$t('edit')"
            />
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5" aria-label="history">
        <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
          {{ $t("sarmaya_section_history") }}
        </h2>
        <div class="flex flex-col gap-3 mb-3">
          <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <BaseInput
              :modelValue="$store.state.sarmaya.search"
              @update:modelValue="onSearch"
              :placeholder="$t('search')"
              :prefixIcon="'fa-solid fa-search'"
              class="w-full min-w-0 sm:flex-1 sm:max-w-md"
            />
            <button
              type="button"
              class="shrink-0 rounded-lg bg-primaryColor px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 w-full sm:w-auto disabled:opacity-60"
              @click="printPage"
              :disabled="isPrintLoading"
              v-if="$store.getters['checkPrintAccess']"
            >
              <i v-if="isPrintLoading" class="fa-solid fa-spinner fa-spin mr-1"></i>
              {{ $t("print") }}
            </button>
          </div>
          <div
            class="sarmaya-table-filters flex flex-wrap items-end gap-x-2 gap-y-2 border-t border-gray-100 pt-3 dark:border-gray-700"
          >
            <div class="w-full min-w-[10rem] max-w-[14rem] flex-1 sm:flex-initial">
              <DropdownInput
                v-model="filters.target_user_id"
                :items="usersList"
                :label="$t('sarmaya_user')"
                :placeholder="$t('sarmaya_user')"
                :rules="[]"
                size="sm"
                @change="applyFilters"
              />
            </div>
            <div class="w-[min(100%,11rem)]">
              <DropdownInput
                v-model="filters.cur_id"
                :items="filterCurrencyList"
                :label="$t('sarmaya_currency')"
                :placeholder="$t('sarmaya_currency')"
                :rules="[]"
                size="sm"
                @change="applyFilters"
              />
            </div>
            <div class="w-[min(100%,10rem)] rounded-md p-0.5" :class="filterTypeHighlightClass">
              <DropdownInput
                v-model="filters.sat_type"
                :items="typesList"
                :label="$t('sarmaya_type')"
                :placeholder="$t('sarmaya_type')"
                :rules="[]"
                size="sm"
                @change="applyFilters"
              />
            </div>
            <div class="w-[min(100%,11rem)]">
              <BaseInput
                v-model="filters.start_date"
                type="date"
                :label="$t('sarmaya_from_date')"
                :placeholder="$t('sarmaya_from_date')"
                :rules="[]"
              />
            </div>
            <div class="w-[min(100%,11rem)]">
              <BaseInput
                v-model="filters.end_date"
                type="date"
                :label="$t('sarmaya_to_date')"
                :placeholder="$t('sarmaya_to_date')"
                :rules="[]"
              />
            </div>
            <div class="flex flex-wrap gap-2 pb-0.5">
              <button
                type="button"
                class="rounded-lg bg-primaryColor px-3 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-95 sm:py-2.5 sm:text-sm"
                @click="applyFilters"
              >
                {{ $t("sarmaya_apply_filter") }}
              </button>
              <button
                type="button"
                class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:py-2.5 sm:text-sm"
                @click="resetFilters"
              >
                {{ $t("sarmaya_reset_filter") }}
              </button>
            </div>
          </div>
        </div>
        <div class="w-full overflow-x-auto">
          <EasyDataTable
            v-model:server-options="serverOptions"
            :headers="headers"
            :items="items"
            :server-items-length="serverItemsLength"
            :loading="$store.state.sarmaya.isLoadingData"
            :buttons-pagination="true"
            :header-text-direction="isRtl ? 'right' : 'left'"
            :body-text-direction="isRtl ? 'right' : 'left'"
            :rows-items="[25, 50, 100, serverItemsLength]"
            :table-class-name="isDark ? 'modern-dark-table' : ''"
            :alternating="false"
          >
            <template #item-sat_type="item">
              <span
                class="sarmaya-type-pill inline-flex min-w-[5.5rem] items-center justify-center rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-sm"
                :class="typeBadgeClass(rowSatType(item))"
              >
                {{ typeLabel(rowSatType(item)) }}
              </span>
            </template>
            <template #item-currency="item">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
                {{ currencyLabel(item) }}
              </span>
            </template>
            <template #item-sat_amount="item">
              <span class="inline-flex flex-wrap items-baseline gap-x-1.5 font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                <span>{{ formatNumber(item.sat_amount) }}</span>
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ currencySymbol(item) }}</span>
              </span>
            </template>
            <template #item-target_user="item">
              {{ item.target_user ? item.target_user.name : "—" }}
            </template>
            <template #item-actor="item">
              {{ item.actor ? item.actor.name : "—" }}
            </template>
            <template #item-sat_note="item">
              <span class="line-clamp-2">{{ item.sat_note || "—" }}</span>
            </template>
            <template #item-created_at="item">
              {{ formatDate(item.created_at) }}
            </template>
            <template #item-actions="item">
              <button class="mx-1" @click="startEdit(item)" v-if="can('edit')" type="button">
                <i
                  class="fa-solid fa-pen text-center p-1 w-7 h-7 text-sm bg-blue-200 dark:bg-blue-300 rounded-md text-blue-500"
                ></i>
              </button>
              <button class="mx-1" @click="removeItem(item.sat_id)" v-if="can('delete')" type="button">
                <i
                  class="fa-solid fa-trash text-center p-1 w-7 h-7 text-sm bg-red-200 dark:bg-red-300 rounded-md text-red-500"
                ></i>
              </button>
            </template>
            <template #loading>
              <div class="flex justify-center items-center py-10">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            </template>
            <template #empty-message>
              <div class="flex flex-col items-center justify-center py-10 text-gray-500 dark:text-gray-400">
                <i class="fa-solid fa-inbox text-3xl mb-2 opacity-60"></i>
                <span>{{ $t("no_data_found") }}</span>
              </div>
            </template>
          </EasyDataTable>
        </div>
      </section>
    </div>
  </div>
  <confirm-dialogue ref="confirmDialogue"></confirm-dialogue>
</template>

<script>
import HeaderApp from "../../components/Navbar/header-app.vue";
import BaseInput from "../../components/Inputs/BaseInput.vue";
import ButtonInput from "../../components/Inputs/ButtonInput.vue";
import DropdownInput from "../../components/Inputs/DropdownInput.vue";
import ConfirmDialogue from "../../components/Modals/ConfirmDialogue.vue";
import LoadingProgress from "../../components/loading-progress.vue";
import { formatDate, formatNumber } from "../../utils/functions";
import { CURRENCYS } from "../../constants/config";
import "../../assets/css/modern-table.css";

export default {
  name: "SarmayaPage",
  components: { HeaderApp, BaseInput, ButtonInput, DropdownInput, ConfirmDialogue, LoadingProgress },
  data() {
    return {
      rules: [(v) => !!v || this.$t("you_must_fill_this_field")],
      serverOptions: { page: 1, rowsPerPage: 25 },
      serverItemsLength: 0,
      items: [],
      summary: {},
      filters: { target_user_id: "", sat_type: "", cur_id: "", start_date: "", end_date: "" },
      form: { sat_id: null, target_user_id: "", sat_type: "", cur_id: "2", sat_amount: "", sat_note: "" },
      isEditing: false,
      isPageLoading: true,
      isPrintLoading: false,
      bootstrapped: false,
    };
  },
  computed: {
    isDark() {
      return this.$store.state.theme === "dark";
    },
    isRtl() {
      return this.$store.getters["isRtl"];
    },
    hasPerTarget() {
      return (this.summary.per_target_user || []).length > 0;
    },
    summaryTotalsList() {
      const raw = this.summary.totals_by_currency;
      if (raw && typeof raw === "object" && Object.keys(raw).length) {
        return Object.keys(raw)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => ({ key: k, ...raw[k] }));
      }
      return CURRENCYS.map((c) => ({
        key: String(c.cur_id),
        cur_id: c.cur_id,
        currency: { cur_id: c.cur_id, cur_name: c.cur_name, cur_symbol: c.cur_symbol },
        total_deposit: 0,
        total_withdraw: 0,
        total_now: 0,
      }));
    },
    perTargetByUserList() {
      const list = this.summary.per_target_user || [];
      const byUser = {};
      for (const item of list) {
        const uid = item.target_user_id != null ? String(item.target_user_id) : "_";
        if (!byUser[uid]) {
          byUser[uid] = {
            target_user_id: item.target_user_id,
            target_user: item.target_user,
            byCur: {},
          };
        }
        byUser[uid].byCur[String(item.cur_id != null ? item.cur_id : 1)] = item;
      }
      return Object.values(byUser)
        .sort((a, b) => {
          const na = (a.target_user && a.target_user.name) || "";
          const nb = (b.target_user && b.target_user.name) || "";
          return na.localeCompare(nb, undefined, { sensitivity: "base" });
        })
        .map((ug) => ({
          target_user_id: ug.target_user_id,
          target_user: ug.target_user,
          columns: CURRENCYS.map((c) => {
            const curId = String(c.cur_id);
            const existing = ug.byCur[curId];
            const item =
              existing ||
              {
                cur_id: c.cur_id,
                total_deposit: 0,
                total_withdraw: 0,
                net_total: 0,
                currency: { cur_id: c.cur_id, cur_name: c.cur_name, cur_symbol: c.cur_symbol },
              };
            return {
              curId,
              label: `${this.$t(c.cur_name)} (${c.cur_symbol})`,
              item,
            };
          }),
        }));
    },
    formTypeDdClass() {
      if (this.isWithdraw(this.form.sat_type)) return "sarmaya-form-type-dd--withdraw";
      if (this.isDeposit(this.form.sat_type)) return "sarmaya-form-type-dd--deposit";
      return "";
    },
    filterCurrencyList() {
      return [
        { id: "", value: this.$t("sarmaya_all_currencies") },
        ...CURRENCYS.map((c) => ({
          id: String(c.cur_id),
          value: `${this.$t(c.cur_name)} (${c.cur_symbol})`,
        })),
      ];
    },
    formCurrencyList() {
      return [
        { id: "", value: this.$t("sarmaya_select_currency") },
        ...CURRENCYS.map((c) => ({
          id: String(c.cur_id),
          value: `${this.$t(c.cur_name)} (${c.cur_symbol})`,
        })),
      ];
    },
    filterTypeHighlightClass() {
      if (this.filters.sat_type === "withdraw") {
        return "sarmaya-filter-type-dd sarmaya-form-type-dd--withdraw";
      }
      if (this.filters.sat_type === "deposit") {
        return "sarmaya-filter-type-dd sarmaya-form-type-dd--deposit";
      }
      return "";
    },
    usersList() {
      return (this.$store.state.sarmaya.users || []).map((u) => ({ id: String(u.id), value: u.name }));
    },
    typesList() {
      return [
        { id: "", value: this.$t("sarmaya_select_type") },
        { id: "deposit", value: this.$t("sarmaya_deposit") },
        { id: "withdraw", value: this.$t("sarmaya_withdraw") },
      ];
    },
    headers() {
      return [
        { text: "#", value: "sat_id", sortable: true },
        { text: this.$t("sarmaya_type"), value: "sat_type", sortable: true },
        { text: this.$t("sarmaya_currency"), value: "currency", sortable: false },
        { text: this.$t("sarmaya_target_user"), value: "target_user", sortable: false },
        { text: this.$t("sarmaya_amount"), value: "sat_amount", sortable: true },
        { text: this.$t("admin"), value: "actor", sortable: false },
        { text: this.$t("note"), value: "sat_note", sortable: true },
        { text: this.$t("created_at"), value: "created_at", sortable: true },
        { text: this.$t("actions"), value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    serverOptions: {
      handler() {
        if (!this.bootstrapped) return;
        this.loadFromServer();
      },
      deep: true,
    },
  },
  async mounted() {
    if (!this.can("view")) {
      this.isPageLoading = false;
      this.bootstrapped = true;
      return;
    }
    try {
      await this.$store.dispatch("sarmaya/getUsers");
      await this.refreshSummary();
      await this.loadFromServer();
    } finally {
      this.isPageLoading = false;
      this.bootstrapped = true;
    }
  },
  methods: {
    can(action) {
      return this.$store.getters["checkUserAccess"]({ id: "46", action });
    },
    formatNumber(number) {
      return formatNumber(number);
    },
    formatDate(date) {
      return formatDate(date);
    },
    isDeposit(type) {
      return String(type || "").toLowerCase() === "deposit";
    },
    isWithdraw(type) {
      return String(type || "").toLowerCase() === "withdraw";
    },
    rowSatType(row) {
      if (!row || typeof row !== "object") return "";
      return row.sat_type ?? row.satType ?? "";
    },
    typeBadgeClass(t) {
      const s = String(t || "").toLowerCase();
      if (s === "deposit") return "bg-emerald-600";
      if (s === "withdraw") return "bg-red-600";
      return "bg-gray-500";
    },
    typeLabel(type) {
      if (this.isDeposit(type)) return this.$t("sarmaya_deposit");
      if (this.isWithdraw(type)) return this.$t("sarmaya_withdraw");
      return type || "—";
    },
    currencySectionTitle(block) {
      if (block.currency) {
        return `${this.$t(block.currency.cur_name)} (${block.currency.cur_symbol})`;
      }
      return this.currencySectionTitleFromId(block.key);
    },
    currencySectionTitleFromId(cid) {
      const c = CURRENCYS.find((x) => String(x.cur_id) === String(cid));
      if (c) return `${this.$t(c.cur_name)} (${c.cur_symbol})`;
      const row = (this.summary.totals_by_currency || {})[cid];
      if (row && row.currency) {
        return `${this.$t(row.currency.cur_name)} (${row.currency.cur_symbol})`;
      }
      return String(cid);
    },
    currencyLabel(item) {
      if (item.currency) {
        return `${this.$t(item.currency.cur_name)} (${item.currency.cur_symbol})`;
      }
      const c = CURRENCYS.find((x) => String(x.cur_id) === String(item.cur_id));
      if (c) return `${this.$t(c.cur_name)} (${c.cur_symbol})`;
      return "—";
    },
    currencySymbol(row) {
      if (!row || typeof row !== "object") return "";
      if (row.currency && row.currency.cur_symbol) return row.currency.cur_symbol;
      const id = row.cur_id != null ? row.cur_id : row.key;
      const c = CURRENCYS.find((x) => String(x.cur_id) === String(id));
      return c ? c.cur_symbol : "";
    },
    async loadFromServer() {
      const payload = {
        ...this.serverOptions,
        search: this.$store.state.sarmaya.search,
        ...this.filters,
      };
      const res = await this.$store.dispatch("sarmaya/getItems", payload);
      if (res && res.data && Array.isArray(res.data.data)) {
        this.items = res.data.data;
        this.serverItemsLength = res.data.total;
      } else {
        this.items = [];
        this.serverItemsLength = 0;
      }
    },
    async refreshSummary() {
      const res = await this.$store.dispatch("sarmaya/getSummary", this.filters);
      this.summary = (res && res.data) || {};
    },
    onSearch(value) {
      this.$store.commit("sarmaya/SET_SEARCH", value);
      this.serverOptions.page = 1;
      this.loadFromServer();
    },
    async applyFilters() {
      this.serverOptions.page = 1;
      await this.loadFromServer();
      await this.refreshSummary();
    },
    async resetFilters() {
      this.filters = { target_user_id: "", sat_type: "", cur_id: "", start_date: "", end_date: "" };
      this.serverOptions.page = 1;
      await this.loadFromServer();
      await this.refreshSummary();
    },
    resetForm() {
      this.form = { sat_id: null, target_user_id: "", sat_type: "", cur_id: "2", sat_amount: "", sat_note: "" };
      this.isEditing = false;
    },
    async createItem() {
      if (!this.form.target_user_id || !this.form.sat_type || !this.form.sat_amount || !this.form.cur_id) return;
      const payload = {
        target_user_id: Number(this.form.target_user_id),
        sat_type: this.form.sat_type,
        cur_id: Number(this.form.cur_id),
        sat_amount: this.form.sat_amount,
        sat_note: this.form.sat_note,
      };
      const res = await this.$store.dispatch("sarmaya/addItem", payload);
      if (res.status === 200) {
        this.resetForm();
        await this.loadFromServer();
        await this.refreshSummary();
      }
    },
    startEdit(item) {
      this.isEditing = true;
      this.form = {
        sat_id: item.sat_id,
        target_user_id: String(item.target_user_id),
        sat_type: item.sat_type,
        cur_id: String(item.cur_id != null ? item.cur_id : 2),
        sat_amount: item.sat_amount,
        sat_note: item.sat_note,
      };
    },
    async updateItem() {
      const res = await this.$store.dispatch("sarmaya/editItem", this.form);
      if (res.status === 200) {
        this.resetForm();
        await this.loadFromServer();
        await this.refreshSummary();
      }
    },
    async removeItem(id) {
      const ok = await this.$refs.confirmDialogue.show({
        message: this.$t("are_you_sure_remove_this_item") + id,
        okButton: this.$t("delete"),
      });
      if (!ok) return;
      const res = await this.$store.dispatch("sarmaya/deleteItem", id);
      if (res.status === 200) {
        await this.loadFromServer();
        await this.refreshSummary();
      }
    },
    async printPage() {
      if (this.isPrintLoading) return;
      this.isPrintLoading = true;
      try {
        const rows = await this.fetchAllTransactionsForPrint();
        const html = this.buildSarmayaPrintHtml(rows);
        this.printContent(html);
      } catch (error) {
        console.error("Sarmaya print error:", error);
      } finally {
        this.isPrintLoading = false;
      }
    },
    async fetchAllTransactionsForPrint() {
      const total = Math.max(Number(this.serverItemsLength) || 0, 1);
      const payload = {
        page: 1,
        rowsPerPage: total,
        search: this.$store.state.sarmaya.search,
        sortBy: "sat_id",
        sortType: "desc",
        ...this.filters,
      };
      const res = await this.$store.dispatch("sarmaya/getItems", payload);
      if (res && res.data && Array.isArray(res.data.data)) {
        return res.data.data;
      }
      return [];
    },
    getFilterDisplayValue(key) {
      const f = this.filters;
      if (key === "target_user_id") {
        if (!f.target_user_id) return this.$t("all");
        const u = this.usersList.find((x) => x.id === String(f.target_user_id));
        return u ? u.value : String(f.target_user_id);
      }
      if (key === "cur_id") {
        if (!f.cur_id) return this.$t("sarmaya_all_currencies");
        const c = this.filterCurrencyList.find((x) => x.id === String(f.cur_id));
        return c ? c.value : String(f.cur_id);
      }
      if (key === "sat_type") {
        if (!f.sat_type) return this.$t("all");
        return this.typeLabel(f.sat_type);
      }
      if (key === "start_date") {
        return f.start_date ? this.formatDate(f.start_date) : this.$t("all_time");
      }
      if (key === "end_date") {
        return f.end_date ? this.formatDate(f.end_date) : this.$t("all_time");
      }
      if (key === "search") {
        const q = (this.$store.state.sarmaya.search || "").trim();
        return q || this.$t("all");
      }
      return "—";
    },
    escapeHtml(value) {
      if (value === undefined || value === null) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    buildSarmayaPrintHtml(rows) {
      const settings = this.$store.getters["currentSettingWiseManager"] || {};
      const fileUrl = this.$store.getters["fileUrl"] || "";
      const appName = settings.app_name || "";
      const logoHtml = settings.set_logo
        ? `<img src="${fileUrl}${settings.set_logo}" alt="" style="max-height:70px;max-width:180px;" />`
        : "";
      const isRtl = this.isRtl;
      const dir = isRtl ? "rtl" : "ltr";
      const textAlign = isRtl ? "right" : "left";

      const summaryRows = this.summaryTotalsList
        .map((block) => {
          const title = this.escapeHtml(this.currencySectionTitle(block));
          const sym = this.escapeHtml(this.currencySymbol(block));
          return `
            <tr>
              <td>${title}</td>
              <td class="dep">${this.formatNumber(block.total_deposit || 0)} ${sym}</td>
              <td class="wit">${this.formatNumber(block.total_withdraw || 0)} ${sym}</td>
              <td><b>${this.formatNumber(block.total_now || 0)} ${sym}</b></td>
            </tr>`;
        })
        .join("");

      const perTargetRows = this.hasPerTarget
        ? this.perTargetByUserList
            .map((userGrp) => {
              const userName = this.escapeHtml(
                userGrp.target_user ? userGrp.target_user.name : "—"
              );
              const cells = userGrp.columns
                .map((col) => {
                  const sym = this.escapeHtml(this.currencySymbol(col.item));
                  const net = col.item.net_total != null ? col.item.net_total : 0;
                  return `
                  <div class="pt-cur">
                    <div class="pt-cur-title">${this.escapeHtml(col.label)}</div>
                    <div><span class="lbl">${this.$t("sarmaya_total_deposit")}:</span> <span class="dep">${this.formatNumber(col.item.total_deposit || 0)} ${sym}</span></div>
                    <div><span class="lbl">${this.$t("sarmaya_total_withdraw")}:</span> <span class="wit">${this.formatNumber(col.item.total_withdraw || 0)} ${sym}</span></div>
                    <div><span class="lbl">${this.$t("sarmaya_total_now")}:</span> <b>${this.formatNumber(net)} ${sym}</b></div>
                  </div>`;
                })
                .join("");
              return `<div class="pt-card"><div class="pt-user">${userName}</div>${cells}</div>`;
            })
            .join("")
        : `<p class="muted">${this.$t("sarmaya_per_target_user_empty")}</p>`;

      const txRows = rows.length
        ? rows
            .map((item) => {
              const type = this.rowSatType(item);
              const typeClass = this.isDeposit(type) ? "dep" : this.isWithdraw(type) ? "wit" : "";
              return `
            <tr>
              <td>${this.escapeHtml(item.sat_id)}</td>
              <td class="${typeClass}">${this.escapeHtml(this.typeLabel(type))}</td>
              <td>${this.escapeHtml(this.currencyLabel(item))}</td>
              <td>${this.escapeHtml(item.target_user ? item.target_user.name : "—")}</td>
              <td><b>${this.formatNumber(item.sat_amount)}</b> ${this.escapeHtml(this.currencySymbol(item))}</td>
              <td>${this.escapeHtml(item.actor ? item.actor.name : "—")}</td>
              <td>${this.escapeHtml(item.sat_note || "—")}</td>
              <td>${this.escapeHtml(this.formatDate(item.created_at))}</td>
            </tr>`;
            })
            .join("")
        : `<tr><td colspan="8" class="muted">${this.$t("no_data_found")}</td></tr>`;

      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${this.escapeHtml(this.$t("sarmaya_title"))}</title>
  <style>
    @font-face {
      font-family: 'kurd';
      src: url('https://storage.wisesale.app/Rabar_022-Busi21Jx.ttf') format('truetype');
    }
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body {
      font-family: 'kurd', sans-serif;
      direction: ${dir};
      text-align: ${textAlign};
      color: #111;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .wrap { max-width: 210mm; margin: 0 auto; padding: 4mm; }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      border-bottom: 2px solid #333;
      padding-bottom: 10px;
      margin-bottom: 14px;
    }
    .header .meta { font-size: 13px; line-height: 1.6; }
    .header .title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
    .section { margin-bottom: 16px; }
    .section h2 {
      font-size: 15px;
      margin: 0 0 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #ccc;
    }
    .filters {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px 16px;
      font-size: 13px;
      background: #f7f7f7;
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 10px;
    }
    .filters div b { display: inline-block; min-width: 7rem; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      vertical-align: top;
    }
    th {
      background: #4f46e5;
      color: #fff;
      font-weight: 700;
    }
    tr:nth-child(even) td { background: #fafafa; }
    .dep { color: #059669; font-weight: 600; }
    .wit { color: #dc2626; font-weight: 600; }
    .muted { color: #666; text-align: center; }
    .pt-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }
    .pt-card {
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px;
      font-size: 12px;
      break-inside: avoid;
    }
    .pt-user {
      font-weight: 700;
      border-bottom: 1px solid #eee;
      padding-bottom: 4px;
      margin-bottom: 6px;
    }
    .pt-cur { margin-bottom: 6px; }
    .pt-cur-title { font-weight: 600; color: #444; margin-bottom: 2px; }
    .pt-cur .lbl { color: #555; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="header">
      <div>
        <h1 class="title">${this.escapeHtml(this.$t("sarmaya_title"))}</h1>
        <div class="meta">
          <div><b>${this.$t("admin")}:</b> ${this.escapeHtml((this.$store.getters["user"] && this.$store.getters["user"].name) || "—")}</div>
          <div><b>${this.$t("date")}:</b> ${this.escapeHtml(this.$store.getters["date"])}</div>
          <div><b>${this.$t("time")}:</b> ${this.escapeHtml(this.$store.getters["time"])}</div>
        </div>
      </div>
      <div style="text-align:center;">
        ${logoHtml}
        <div style="font-size:18px;font-weight:700;margin-top:4px;">${this.escapeHtml(appName)}</div>
      </div>
    </div>

    <div class="section">
      <h2>${this.$t("sarmaya_section_filters")}</h2>
      <div class="filters">
        <div><b>${this.$t("sarmaya_user")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("target_user_id"))}</div>
        <div><b>${this.$t("sarmaya_currency")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("cur_id"))}</div>
        <div><b>${this.$t("sarmaya_type")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("sat_type"))}</div>
        <div><b>${this.$t("search")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("search"))}</div>
        <div><b>${this.$t("sarmaya_from_date")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("start_date"))}</div>
        <div><b>${this.$t("sarmaya_to_date")}:</b> ${this.escapeHtml(this.getFilterDisplayValue("end_date"))}</div>
      </div>
    </div>

    <div class="section">
      <h2>${this.$t("sarmaya_title")} — ${this.$t("sarmaya_total_now")}</h2>
      <table>
        <thead>
          <tr>
            <th>${this.$t("sarmaya_currency")}</th>
            <th>${this.$t("sarmaya_total_deposit")}</th>
            <th>${this.$t("sarmaya_total_withdraw")}</th>
            <th>${this.$t("sarmaya_total_now")}</th>
          </tr>
        </thead>
        <tbody>${summaryRows}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>${this.$t("sarmaya_per_target_user")}</h2>
      <div class="pt-grid">${perTargetRows}</div>
    </div>

    <div class="section">
      <h2>${this.$t("sarmaya_section_history")} (${rows.length})</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>${this.$t("sarmaya_type")}</th>
            <th>${this.$t("sarmaya_currency")}</th>
            <th>${this.$t("sarmaya_target_user")}</th>
            <th>${this.$t("sarmaya_amount")}</th>
            <th>${this.$t("admin")}</th>
            <th>${this.$t("note")}</th>
            <th>${this.$t("created_at")}</th>
          </tr>
        </thead>
        <tbody>${txRows}</tbody>
      </table>
    </div>
  </div>
</body>
</html>`;
    },
    printContent(content) {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      iframe.contentDocument.write(content);
      iframe.contentDocument.close();
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }, 400);
      };
    },
  },
};
</script>

<style scoped>
.sarmaya-form-type-dd.sarmaya-form-type-dd--deposit :deep(input.block),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--deposit :deep(input.block) {
  background-color: #059669 !important;
  color: #fff !important;
  border-color: #059669 !important;
}
.sarmaya-form-type-dd.sarmaya-form-type-dd--deposit :deep(input:focus),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--deposit :deep(input:focus) {
  --tw-ring-color: rgb(16 185 129) !important;
  border-color: #10b981 !important;
}
.sarmaya-form-type-dd.sarmaya-form-type-dd--deposit :deep(.fa-chevron-down),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--deposit :deep(.fa-chevron-down) {
  color: rgb(255 255 255 / 0.92) !important;
}
.sarmaya-form-type-dd.sarmaya-form-type-dd--withdraw :deep(input.block),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--withdraw :deep(input.block) {
  background-color: #dc2626 !important;
  color: #fff !important;
  border-color: #dc2626 !important;
}
.sarmaya-form-type-dd.sarmaya-form-type-dd--withdraw :deep(input:focus),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--withdraw :deep(input:focus) {
  --tw-ring-color: rgb(248 113 113) !important;
  border-color: #ef4444 !important;
}
.sarmaya-form-type-dd.sarmaya-form-type-dd--withdraw :deep(.fa-chevron-down),
.sarmaya-filter-type-dd.sarmaya-form-type-dd--withdraw :deep(.fa-chevron-down) {
  color: rgb(255 255 255 / 0.92) !important;
}

:deep(.sarmaya-type-pill.bg-emerald-600) {
  background-color: #059669 !important;
  color: #fff !important;
}
:deep(.sarmaya-type-pill.bg-red-600) {
  background-color: #dc2626 !important;
  color: #fff !important;
}
:deep(.sarmaya-type-pill.bg-gray-500) {
  background-color: #6b7280 !important;
  color: #fff !important;
}

.sarmaya-txt-d,
.sarmaya-txt-d span {
  color: #059669 !important;
}
.sarmaya-txt-d-label {
  color: #047857 !important;
}
.sarmaya-icon-d,
.sarmaya-icon-d i {
  color: #059669 !important;
}
html.dark .sarmaya-txt-d,
html.dark .sarmaya-txt-d span {
  color: #34d399 !important;
}
html.dark .sarmaya-txt-d-label {
  color: #6ee7b7 !important;
}
html.dark .sarmaya-icon-d,
html.dark .sarmaya-icon-d i {
  color: #34d399 !important;
}

.sarmaya-txt-w,
.sarmaya-txt-w span {
  color: #dc2626 !important;
}
.sarmaya-txt-w-label {
  color: #b91c1c !important;
}
.sarmaya-icon-w,
.sarmaya-icon-w i {
  color: #dc2626 !important;
}
html.dark .sarmaya-txt-w,
html.dark .sarmaya-txt-w span {
  color: #f87171 !important;
}
html.dark .sarmaya-txt-w-label {
  color: #fca5a5 !important;
}
html.dark .sarmaya-icon-w,
html.dark .sarmaya-icon-w i {
  color: #f87171 !important;
}

.sarmaya-table-filters :deep(.mb-4) {
  margin-bottom: 0.125rem !important;
}
.sarmaya-table-filters :deep(label) {
  font-size: 0.75rem;
  line-height: 1.1rem;
  margin-bottom: 0.125rem !important;
}
</style>
